import React, {
  Attributes,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  useEffect,
  useImperativeHandle,
} from 'react';
import {
  Controller, useForm, FormProvider, UseFormReturn,
} from 'react-hook-form';

import classNames from 'classnames';

import { useBreakpoint } from '@ast/magma/breakpoints';

import { Alert } from '@ast/magma/components/alert';
import { FormField } from '@ast/magma/components/formfield';

import { FieldErrors } from 'react-hook-form/dist/types/errors';
import { RegisterOptions } from 'react-hook-form/dist/types/validator';

import { OverlaySpinner } from '@app/common/components/overlayspinner';

import styles
  from './FormWrapper.pcss';

export type FormFieldsItem = {
  value: any;
  rules?: RegisterOptions;
  assistive?: string;
  control: ReactElement;
  title?: string;
  isAutoTrim?: boolean;
};

export type FormsFields = Record<string, FormFieldsItem>;
export type FormsSubmitCallback<Values = Record<string, any>> = (
  values:Values, isChanged: boolean
) => Promise<void> | void;

interface FormWrapperProps {
  readonly formId: string;
  readonly fields?: FormsFields;
  readonly setFocusTo?: string;
  readonly errorMessage?: string | null;
  readonly successMessage?: string | null;
  readonly formFieldClassName?: string;
  readonly isPending?: boolean;
  readonly onSubmitCallback?: FormsSubmitCallback;
  readonly className?: string;
}

const getControlElement = (
  element: ReactElement,
  props: HTMLAttributes<ReactElement>,
) => React.cloneElement(element, props);

export const getValidationError = (
  errors: Record<string, FieldErrors>,
  fieldKey: string,
) => errors[fieldKey]?.message || null;

export const getDefaultValues = (
  fields: FormsFields,
) => Object.keys(fields).reduce((prev, current) => {
  const item = { [current]: fields[current].value };
  return Object.assign(prev, item);
}, {});

export const FormWrapper = forwardRef<UseFormReturn, React.PropsWithChildren<FormWrapperProps>>(({
  formId,
  fields,
  setFocusTo,
  errorMessage,
  successMessage,
  isPending,
  onSubmitCallback,
  children,
  formFieldClassName,
  className,
}, formRef) => {
  const isMobile = useBreakpoint().device === 'mobile';

  const methods = useForm({
    mode: 'onChange',
    defaultValues: fields ? getDefaultValues(fields) : {},
  });

  useImperativeHandle(formRef, () => methods, []);

  const {
    handleSubmit,
    formState: { errors, isDirty },
    control,
    setFocus,
    setValue,
  } = methods;

  useEffect(() => {
    if (setFocusTo && !isMobile) {
      setFocus(setFocusTo as never);
    }
  }, []);

  const onSubmit = async (values:FormsSubmitCallback) => {
    if (onSubmitCallback) {
      await onSubmitCallback(values, isDirty);
    }
  };

  return (
    <FormProvider {...methods}>
      <form
        className={className}
        onSubmit={handleSubmit(onSubmit)}
        id={formId}
        data-stable-name="FormWrapper"
        noValidate
      >
        {errorMessage && (
          <Alert data-stable-name="ErrorMessage" level="error" className={styles.alert}>
            <span>{ errorMessage }</span>
          </Alert>
        )}

        {successMessage && (
          <Alert data-stable-name="SuccessMessage" level="success" className={styles.alert}>
            <span>{ successMessage }</span>
          </Alert>
        )}

        {isPending && (
          <OverlaySpinner
            data-stable-name="FormWrapperSpinner"
            containerClassName={styles.spinner}
            visible
          />
        )}

        {(fields && Object.keys(fields).length) ? Object.keys(fields).map((key: string) => {
          const validationError = getValidationError(errors, key);
          return (
            <Controller
              key={key}
              name={key as never}
              render={({ field }) => (
                <>
                  { fields[key]?.title && (
                  <p className={classNames(styles.title)}>
                    {fields[key]?.title}
                  </p>
                  )}
                  <FormField
                    assistive={fields[key]?.assistive}
                    className={classNames(styles.field, formFieldClassName)}
                    error={validationError}
                    reserveErrorHeight={false}
                  >
                    {
                    getControlElement(
                      fields[key].control,
                      {
                        ...field as Attributes,
                        onBlur: (e) => {
                          if (fields[key].isAutoTrim) {
                            const target = e.target as unknown as HTMLInputElement;
                            setValue(key as never, target.value.trim() as never, { shouldValidate: true });
                          }
                          field.onBlur();
                        },
                        className: classNames(styles.input),
                        // eslint-disable-next-line i18next/no-literal-string
                        'aria-invalid': validationError,
                      },
                    )
                  }
                  </FormField>
                </>
              )}
              rules={fields[key].rules}
              control={control}
            />
          );
        })
          : null}

        { children && (
          <section className={styles.content}>
            {children}
          </section>
        )}
      </form>
    </FormProvider>
  );
});
